import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NotificationType } from '@app/shared/interfaces/lib/notification.interface';
import { PricesAndFees, PricesAndFeesGroups } from '@app/shared/models/tax';
import { PropertiesService } from '@app/shared/services/properties/properties.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { BehaviorSubject, throwError, timer } from 'rxjs';
import { catchError, delay, filter, map, switchMap, take, tap } from 'rxjs/operators';

// Property pricing state service
@Injectable({
  providedIn: 'any',
})
export class PropertyPricingService {
  public propertyId$ = this.route.paramMap.pipe(
    filter((params) => params.has('id')),
    map((params) => params.get('id'))
  );
  public loadingPricingFeeDiscount$ = new BehaviorSubject(false);
  public pricingFeeDiscountData$ = this.propertyId$.pipe(
    tap(() => this.loadingPricingFeeDiscount$.next(true)),
    switchMap((propertyId) => this.propertiesService.fetchPropertyFeesDiscounts(propertyId)),
    tap((res: any) => {
      this.loadingPricingFeeDiscount$.next(false);
      if (res.error) {
        this.toast.open(
          'There was a problem fetching Fee & Discount data for this property.',
          'Dismiss',
          NotificationType.Error
        );
        throwError(res.err);
      }
    }),
    map((res) => {
      const updated = { ...res };

      // Convert base price from cents
      if (res.base_price?.value) {
        updated.base_price.value = res.base_price.value / 100;
      }
      // Convert weekend base price from cents
      if (res.weekend_price.value) {
        updated.weekend_price.value = res.weekend_price.value / 100;
      }
      return updated;
    })
  );

  public priceSaveState$: BehaviorSubject<any> = new BehaviorSubject(
    PricesAndFeesGroups.reduce((acc, key) => ({ ...acc, [key]: 0 }), {})
  );

  public saved$ = new BehaviorSubject(null);

  constructor(
    private route: ActivatedRoute,
    private propertiesService: PropertiesService,
    private toast: ToastNotificationsService
  ) {}

  public savePriceChange(key: string, data: any) {
    // Copy the data so that updating doesn't mutate what's in the template
    const updatedData = { ...data };
    // Convert base price to cents
    if (key === 'base_price' || key === 'weekend_price') {
      updatedData.value = data.value * 100;

      if (updatedData.value === 0) {
        updatedData.value = null;
      }
    }

    this.propertyId$
      .pipe(
        tap(() => this.updateSaveState(key, 1)),
        switchMap((propertyId) => this.propertiesService.updatePropertyFeesDiscounts(propertyId, key, updatedData)),
        tap(() => {
          this.updateSaveState(key, 2);
          this.saved$.next(updatedData);
        }),
        delay(2000),
        catchError((err) => {
          // if there's an error, set the error status and wait 5 seconds before completing
          this.updateSaveState(key, -1);
          this.toast.open(
            err.message ?? 'There was a problem saving the Fee & Discount data for this property.',
            'Dismiss',
            NotificationType.Error
          );
          return timer(5000).pipe(take(1));
        })
      )
      .subscribe({
        next: () => {
          this.updateSaveState(key);
        },
        complete: () => {
          // reset our saving status
          this.updateSaveState(key);
        },
      });
  }

  public savePriceChanges(changes: Array<{ key: string; data: any }>) {
    // Copy the data for each change to avoid mutating template data
    const updatedChanges = changes.map(({ key, data }) => {
      const updatedData = { ...data };

      // Convert base price and weekend price to cents
      if (key === 'base_price' || key === 'weekend_price') {
        updatedData.value = data.value * 100;

        if (updatedData.value === 0) {
          updatedData.value = null;
        }
      }

      return { key, data: updatedData };
    });

    // Set all relevant groups to saving state
    updatedChanges.forEach(({ key }) => {
      this.updateSaveState(key, 1);
    });

    this.propertyId$
      .pipe(
        switchMap((propertyId) =>
          this.propertiesService.updateMultiplePropertyFeesDiscounts(propertyId, updatedChanges)
        ),
        tap(() => {
          // Set all to saved state
          updatedChanges.forEach(({ key }) => {
            this.updateSaveState(key, 2);
          });
          this.saved$.next(updatedChanges);
        }),
        delay(2000),
        catchError((err) => {
          // Set error state for all changes
          updatedChanges.forEach(({ key }) => {
            this.updateSaveState(key, -1);
          });

          this.toast.open(
            err.message ?? 'There was a problem saving the Fee & Discount data for this property.',
            'Dismiss',
            NotificationType.Error
          );
          return timer(5000).pipe(take(1));
        })
      )
      .subscribe({
        next: () => {
          // Reset state for all changes
          updatedChanges.forEach(({ key }) => {
            this.updateSaveState(key, 0);
          });
        },
        complete: () => {
          // Reset state for all changes
          updatedChanges.forEach(({ key }) => {
            this.updateSaveState(key, 0);
          });
        },
      });
  }

  private updateSaveState(key: string, status = 0) {
    const group = Object.entries(PricesAndFees).find(([_, fields]) => fields.find((field) => field.key === key))?.[0];
    this.priceSaveState$.next({
      ...this.priceSaveState$.value,
      [group]: status,
    });
  }
}
